import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import {
  Card,
  Button,
  IconButton,
} from '../components/ui';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { PlusIcon, PencilIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import FilterableTable from '../components/FilterableTable';
import CompanyDetails from '../components/cms/CompanyDetails';
import CompanyUpload from '../components/cms/CompanyUpload';
import JobHistory from '../components/cms/JobHistory';
import DatabaseList from '../components/cms/DatabaseList';
import DatabaseEditor from '../components/cms/DatabaseEditor';
import BackupsManager from '../components/cms/BackupsManager';

export default function CMS() {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const { tab, id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(tab || 'companies');
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Companies tab state
  const [cmsCompanies, setCmsCompanies] = useState([]);
  const [villages, setVillages] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyMode, setCompanyMode] = useState('view');
  const [jobsRefreshKey, setJobsRefreshKey] = useState(0);

  // Databases tab state
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [databaseMode, setDatabaseMode] = useState('list'); // 'list' | 'edit' | 'new'

  // Form state for groups/users
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    domain: '',
    email: '',
    alias: '',
    groupId: '',
    permissions: {
      routes: [],
      functions: [],
      companies: [],
      dev: false,
      clientMode: false,
      clientCompanyId: '',
      clientCompanyName: '',
    }
  });

  const availableRoutes = ['/', '/dashboard', '/insights', '/clients', '/cms'];

  useEffect(() => {
    if (!permissions || !permissions.CMS_access) {
      navigate('/');
      return;
    }
    loadData();
  }, [permissions, navigate]);

  // Auto-edit: only fires once when the company id first appears in the URL (e.g. from "Edit in CMS" button)
  useEffect(() => {
    if (id && new URLSearchParams(location.search).get('edit') === 'true') {
      setCompanyMode('edit');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // intentionally excludes location.search — only read once per id change

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
    if (id === 'new') {
      setSelectedCompany(null);
      setCompanyMode('new');
    } else if (id && cmsCompanies.length > 0) {
      const company = cmsCompanies.find(c => c.id === id);
      if (company) {
        setSelectedCompany(company);
      }
    } else if (!id) {
      setSelectedCompany(null);
      setSelectedUser(null);
      setSelectedGroup(null);
    }
  }, [tab, id, cmsCompanies]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [groupsRes, usersRes, companiesRes, cmsCompaniesRes, villagesRes, industriesRes, ministriesRes] = await Promise.all([
        api.get('/cms/groups'),
        api.get('/cms/users'),
        api.get('/companies', { params: { limit: 500 } }),
        api.get('/cms/companies'),
        api.get('/cms/villages'),
        api.get('/cms/industries'),
        api.get('/cms/ministries')
      ]);
      setGroups(groupsRes.data);
      setUsers(usersRes.data);
      setAllCompanies(companiesRes.data);
      setCmsCompanies(cmsCompaniesRes.data);
      setVillages(villagesRes.data);
      setIndustries(industriesRes.data);
      setMinistries(ministriesRes.data || []);
    } catch (error) {
      console.error('Error loading CMS data:', error);
      setError('Failed to load CMS data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setDatabaseMode('list');
    setSelectedDatabase(null);
    navigate('/cms');
  };

  const handleSelectCompany = (company) => {
    navigate(`/cms/companies/${company.id}`);
  };

  const handleBackToList = () => {
    navigate('/cms');
  };

  const handleNewCompany = () => {
    setSelectedCompany(null);
    setCompanyMode('new');
  };

  const handleCompanyUpdate = (updatedCompany, mode) => {
    if (mode === 'edit') {
      setCompanyMode('edit');
    } else {
      loadData();
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      if (updatedCompany && updatedCompany.id) {
        navigate(`/cms/companies/${updatedCompany.id}`);
      } else {
        navigate('/cms');
      }
      setCompanyMode('view');
    }
  };

  const handleCompanyDelete = (companyId) => {
    setCmsCompanies(cmsCompanies.filter(c => c.id !== companyId));
    navigate('/cms');
  };

  const handleCompanyCancelEdit = () => {
    if (companyMode === 'new') {
      navigate('/cms');
    }
    setCompanyMode('view');
  };

  const handleUploadComplete = () => {
    loadData();
    setJobsRefreshKey(prev => prev + 1);
  };

  const handleJobReverted = () => {
    loadData();
    setJobsRefreshKey(prev => prev + 1);
  };

  // Group/User handlers
  const handleSelectGroup = (group) => {
    navigate(`/cms/permissions/${group.id}`);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setEditMode(null);
    setFormData({
      ...formData,
      id: user.id,
      email: user.email,
      alias: user.alias || '',
      groupId: user.groupId || '',
      permissions: user.permissions
    });
  };

  const handleEditGroup = () => {
    setFormData({
      ...formData,
      id: selectedGroup.id,
      name: selectedGroup.name,
      domain: selectedGroup.domain,
      permissions: selectedGroup.permissions
    });
    setEditMode('group');
  };

  const handleEditUser = () => {
    setEditMode('user');
  };

  const handleNewGroup = () => {
    setSelectedGroup(null);
    setSelectedUser(null);
    setEditMode('new-group');
    setFormData({
      id: '',
      name: '',
      domain: '',
      email: '',
      alias: '',
      groupId: '',
      permissions: {
        routes: [],
        functions: [],
        companies: [],
        dev: false,
        clientMode: false,
        clientCompanyId: '',
        clientCompanyName: '',
      }
    });
  };

  const handleNewUser = () => {
    if (!selectedGroup) {
      alert('Please select a group first');
      return;
    }
    setSelectedUser(null);
    setEditMode('new-user');
    setFormData({
      id: '',
      name: '',
      domain: '',
      email: '',
      alias: '',
      groupId: selectedGroup.id,
      permissions: {
        routes: [],
        functions: [],
        companies: [],
        dev: false,
        clientMode: false,
        clientCompanyId: '',
        clientCompanyName: '',
      }
    });
  };

  const handleSaveGroup = async () => {
    try {
      if (editMode === 'new-group') {
        const response = await api.post('/cms/groups', {
          name: formData.name,
          domain: formData.domain,
          permissions: formData.permissions
        });
        setGroups([...groups, response.data]);
        navigate(`/cms/permissions/${response.data.id}`);
        setEditMode(null);
      } else {
        const response = await api.put(`/cms/groups/${selectedGroup.id}`, {
          name: formData.name,
          domain: formData.domain,
          permissions: formData.permissions
        });
        setGroups(groups.map(g => g.id === selectedGroup.id ? response.data : g));
        setSelectedGroup(response.data);
        setEditMode(null);
      }
    } catch (error) {
      console.error('Error saving group:', error);
      alert('Failed to save group: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveUser = async () => {
    try {
      if (editMode === 'new-user') {
        const response = await api.post('/cms/users', {
          email: formData.email,
          alias: formData.alias,
          groupId: formData.groupId,
          permissions: formData.permissions
        });
        setUsers([...users, response.data]);
        setSelectedUser(response.data);
        setEditMode(null);
      } else {
        const response = await api.put(`/cms/users/${selectedUser.id}`, {
          alias: formData.alias,
          groupId: formData.groupId,
          permissions: formData.permissions
        });
        setUsers(users.map(u => u.id === selectedUser.id ? response.data : u));
        setSelectedUser(response.data);
        setEditMode(null);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Failed to save user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(`Are you sure you want to delete "${selectedGroup.name}"?`)) {
      return;
    }
    try {
      await api.delete(`/cms/groups/${selectedGroup.id}`);
      setGroups(groups.filter(g => g.id !== selectedGroup.id));
      navigate('/cms');
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Failed to delete group: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to delete "${selectedUser.email}"?`)) {
      return;
    }
    try {
      await api.delete(`/cms/users/${selectedUser.id}`);
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setSelectedUser(null);
      setEditMode(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancel = () => {
    setEditMode(null);
    if (selectedGroup) {
      setFormData({
        ...formData,
        id: selectedGroup.id,
        name: selectedGroup.name,
        domain: selectedGroup.domain,
        permissions: selectedGroup.permissions
      });
    }
    if (selectedUser) {
      setFormData({
        ...formData,
        id: selectedUser.id,
        email: selectedUser.email,
        alias: selectedUser.alias || '',
        groupId: selectedUser.groupId || '',
        permissions: selectedUser.permissions
      });
    }
  };

  // Permission toggle functions
  const toggleRoute = (route) => {
    const routes = formData.permissions.routes || [];
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        routes: routes.includes(route)
          ? routes.filter(r => r !== route)
          : [...routes, route]
      }
    });
  };

  const toggleCompany = (companyId) => {
    const companies = formData.permissions.companies || [];
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        companies: companies.includes(companyId)
          ? companies.filter(c => c !== companyId)
          : [...companies, companyId]
      }
    });
  };

  const toggleAllCompanies = () => {
    const allIds = allCompanies.map(c => c.id);
    const currentCompanies = formData.permissions.companies || [];
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        companies: currentCompanies.length === allIds.length ? [] : allIds
      }
    });
  };

  // Sync selectedGroup when id changes
  useEffect(() => {
    if (activeTab === 'permissions' && id) {
      const group = groups.find(g => g.id === id);
      if (group) {
        setSelectedGroup(group);
        setSelectedUser(null);
        setFormData(prev => ({
          ...prev,
          id: group.id,
          name: group.name,
          domain: group.domain,
          permissions: group.permissions
        }));
      }
    }
  }, [id, groups, activeTab]);

  const usersInSelectedGroup = selectedGroup
    ? users.filter(u => u.groupId === selectedGroup.id)
    : [];

  const getBreadcrumbPath = () => {
    const paths = [];
    if (activeTab === 'companies') {
      paths.push({ label: 'Companies', onClick: handleBackToList });
      if (selectedCompany) {
        paths.push({ label: selectedCompany.name, active: true });
      }
    } else if (activeTab === 'databases') {
      paths.push({ label: 'Databases', onClick: () => { setDatabaseMode('list'); setSelectedDatabase(null); } });
      if (databaseMode === 'new') {
        paths.push({ label: 'New Database', active: true });
      } else if (databaseMode === 'edit' && selectedDatabase) {
        paths.push({ label: selectedDatabase.name, active: true });
      }
    } else if (activeTab === 'permissions') {
      paths.push({ label: 'Users & Groups', onClick: handleBackToList });
      if (selectedGroup) {
        paths.push({ label: selectedGroup.name, onClick: () => setSelectedUser(null), active: !selectedUser });
        if (selectedUser) {
          paths.push({ label: selectedUser.email, active: true });
        }
      }
    }
    return paths;
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card variant="danger">
          <p className="text-danger-dark">{error}</p>
        </Card>
      </div>
    );
  }

  const isDetailView = (activeTab === 'companies' && selectedCompany) ||
                       (activeTab === 'permissions' && selectedGroup) ||
                       (activeTab === 'databases' && (databaseMode === 'edit' || databaseMode === 'new'));

  return (
    <div className="container mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary">
          Content Management System
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="flex overflow-x-auto gap-2 mb-6 border-b border-border">
        <button
          onClick={() => handleTabChange('companies')}
          className={`px-6 py-3 font-medium transition relative ${
            activeTab === 'companies'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Companies
          {activeTab === 'companies' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
          )}
        </button>
        <button
          onClick={() => handleTabChange('databases')}
          className={`px-6 py-3 font-medium transition relative ${
            activeTab === 'databases'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Databases
          {activeTab === 'databases' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
          )}
        </button>
        <button
          onClick={() => handleTabChange('permissions')}
          className={`px-6 py-3 font-medium transition relative ${
            activeTab === 'permissions'
              ? 'text-brand-primary'
              : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
          }`}
        >
          Users & Groups
          {activeTab === 'permissions' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
          )}
        </button>
        {(permissions?.BACKUPS_manage || permissions?.dev) && (
          <button
            onClick={() => handleTabChange('backups')}
            className={`px-6 py-3 font-medium transition relative ${
              activeTab === 'backups'
                ? 'text-brand-primary'
                : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
            }`}
          >
            Backups
            {activeTab === 'backups' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
            )}
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      {isDetailView && (
        <div className="mb-6">
          <Breadcrumb path={getBreadcrumbPath()} />
        </div>
      )}

      {/* Companies Tab */}
      <div className={activeTab !== 'companies' ? 'hidden' : ''}>
          {!selectedCompany && companyMode !== 'new' ? (
            /* List View */
            <>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
                  Companies
                </h2>
                <IconButton
                  icon={PlusIcon}
                  label="New Company"
                  variant="primary"
                  size="md"
                  display="icon"
                  onClick={handleNewCompany}
                />
              </div>
              <Card>
                <FilterableTable
                  data={cmsCompanies.map(company => ({
                    ...company,
                    onClick: () => handleSelectCompany(company)
                  }))}
                  searchFields={['name', 'industry', 'village']}
                  searchPlaceholder="Search companies..."
                  showFilters={true}
                  defaultItemsPerPage={25}
                  defaultSortColumn="name"
                  defaultSortDirection="asc"
                  loading={loading}
                  columns={[
                    {
                      key: 'name',
                      header: 'Company Name',
                      align: 'left',
                      className: 'font-semibold text-text-primary-light dark:text-text-primary cursor-pointer hover:opacity-70 transition',
                      render: (company) => company.name
                    },
                    {
                      key: 'industry',
                      header: 'Industry',
                      align: 'left',
                      className: 'text-text-secondary-light dark:text-text-secondary',
                      render: (company) => company.industry
                    },
                    {
                      key: 'village',
                      header: 'Village',
                      align: 'left',
                      className: 'text-text-secondary-light dark:text-text-secondary',
                      render: (company) => company.village
                    },
                    {
                      key: 'responseCount',
                      header: 'Responses',
                      align: 'right',
                      className: 'text-text-secondary-light dark:text-text-secondary',
                      render: (company) => company.responseCount || 0,
                      sortValue: (company) => company.responseCount || 0
                    }
                  ]}
                />
              </Card>
            </>
          ) : (
            /* Detail View */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Company Details (2 columns) */}
              <div className="lg:col-span-2">
                <CompanyDetails
                  company={selectedCompany}
                  villages={villages}
                  industries={industries}
                  ministries={ministries}
                  onUpdate={handleCompanyUpdate}
                  onDelete={handleCompanyDelete}
                  mode={companyMode}
                  onCancel={handleCompanyCancelEdit}
                  onViewInsights={
                    permissions?.routes?.includes?.('/insights') !== false
                      ? (company) => navigate(`/insights?company=${company.id}`)
                      : undefined
                  }
                />
              </div>

              {/* Right: Upload & History (1 column) */}
              {selectedCompany && companyMode === 'view' && (
                <div className="space-y-6">
                  <CompanyUpload
                    company={selectedCompany}
                    onUploadComplete={handleUploadComplete}
                  />
                  <JobHistory
                    company={selectedCompany}
                    onJobReverted={handleJobReverted}
                    refreshKey={jobsRefreshKey}
                  />
                </div>
              )}
            </div>
          )}
      </div>

      {/* Databases Tab */}
      <div className={activeTab !== 'databases' ? 'hidden' : ''}>
        {databaseMode === 'list' ? (
          <DatabaseList
            onSelect={(db) => { setSelectedDatabase(db); setDatabaseMode('edit'); }}
            onCreateNew={() => { setSelectedDatabase(null); setDatabaseMode('new'); }}
          />
        ) : (
          <DatabaseEditor
            database={databaseMode === 'edit' ? selectedDatabase : null}
            onSave={() => { setDatabaseMode('list'); setSelectedDatabase(null); }}
            onCancel={() => { setDatabaseMode('list'); setSelectedDatabase(null); }}
          />
        )}
      </div>

      {/* Users & Groups Tab */}
      <div className={activeTab !== 'permissions' ? 'hidden' : ''}>
          {!selectedGroup && !editMode ? (
            /* List View */
            <>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
                  Groups
                </h2>
                <IconButton
                  icon={PlusIcon}
                  label="New Group"
                  variant="primary"
                  size="md"
                  display="icon"
                  onClick={handleNewGroup}
                />
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="ml-auto text-xs border border-border rounded px-2 py-1 bg-background text-text-primary focus:outline-none"
                >
                  <option value="">All Sources</option>
                  <option value="cms">CMS</option>
                  <option value="booking">Booking</option>
                </select>
              </div>
              <Card>
                <FilterableTable
                  data={groups
                    .filter(g => !sourceFilter || (g.source || 'cms') === sourceFilter)
                    .map(group => ({
                      ...group,
                      userCount: users.filter(u => u.groupId === group.id).length,
                      onClick: () => handleSelectGroup(group)
                    }))}
                  searchFields={['name', 'domain']}
                  searchPlaceholder="Search groups..."
                  showFilters={false}
                  defaultItemsPerPage={25}
                  loading={loading}
                  columns={[
                    {
                      key: 'name',
                      header: 'Group Name',
                      align: 'left',
                      className: 'font-semibold text-text-primary-light dark:text-text-primary cursor-pointer hover:opacity-70 transition',
                      render: (group) => (
                        <span className="flex items-center gap-2">
                          {group.name}
                          {group.source === 'booking' && (
                            <span className="text-[11px] px-1.5 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                              Booking
                            </span>
                          )}
                        </span>
                      )
                    },
                    {
                      key: 'domain',
                      header: 'Domain',
                      align: 'left',
                      className: 'text-text-secondary-light dark:text-text-secondary',
                      render: (group) => group.domain
                    },
                    {
                      key: 'userCount',
                      header: 'Users',
                      align: 'right',
                      className: 'text-text-secondary-light dark:text-text-secondary',
                      render: (group) => group.userCount,
                      sortValue: (group) => group.userCount
                    }
                  ]}
                />
              </Card>
            </>
          ) : (
            /* Detail View */
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Left: Group Details */}
                <Card>
                  <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
                    {editMode === 'new-group' ? 'New Group' : editMode === 'group' ? 'Edit Group' : 'Group Details'}
                  </h2>

                  {(editMode === 'group' || editMode === 'new-group') ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Name</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-3 py-2 border border-border-light dark:border-border rounded bg-white dark:bg-background-gray text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Domain</label>
                        <input
                          type="text"
                          value={formData.domain}
                          onChange={(e) => setFormData({...formData, domain: e.target.value})}
                          className="w-full px-3 py-2 border border-border-light dark:border-border rounded bg-white dark:bg-background-gray text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent"
                          placeholder="@example.com"
                        />
                      </div>

                      {/* Permissions Section */}
                      <div className="border-t border-border-light dark:border-border pt-4 mt-4">
                        <h3 className="text-md font-semibold text-text-primary-light dark:text-text-primary mb-3">Permissions</h3>

                        <div className="space-y-4">
                          {!formData.permissions.clientMode && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">Routes</label>
                                <div className="space-y-2">
                                  {availableRoutes.map(route => (
                                    <label key={route} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={formData.permissions.routes?.includes(route)}
                                        onChange={() => toggleRoute(route)}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-text-primary-light dark:text-text-primary">{route}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.permissions.dev}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      permissions: { ...formData.permissions, dev: e.target.checked }
                                    })}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-text-primary-light dark:text-text-primary">Dev Access</span>
                                </label>
                              </div>
                            </>
                          )}

                          <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.permissions.clientMode || false}
                                onChange={(e) => {
                                  const enabled = e.target.checked;
                                  const existingId = formData.permissions.clientCompanyId;
                                  setFormData({
                                    ...formData,
                                    permissions: {
                                      ...formData.permissions,
                                      clientMode: enabled,
                                      clientCompanyId: enabled ? existingId : '',
                                      clientCompanyName: enabled ? formData.permissions.clientCompanyName : '',
                                      companies: enabled && existingId ? [existingId] : formData.permissions.companies,
                                    }
                                  });
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-text-primary-light dark:text-text-primary font-medium">Client Mode</span>
                              <span className="text-xs text-text-muted-light dark:text-text-muted">(portal-only access)</span>
                            </label>
                            {formData.permissions.clientMode && (
                              <div className="mt-2 ml-6">
                                <label className="block text-xs text-text-muted-light dark:text-text-muted mb-1">Assigned Company</label>
                                <select
                                  value={formData.permissions.clientCompanyId || ''}
                                  onChange={(e) => {
                                    const company = allCompanies.find(c => c.id === e.target.value);
                                    setFormData({
                                      ...formData,
                                      permissions: {
                                        ...formData.permissions,
                                        clientCompanyId: e.target.value,
                                        clientCompanyName: company?.name || '',
                                        companies: e.target.value ? [e.target.value] : [],
                                      }
                                    });
                                  }}
                                  className="w-full text-sm px-3 py-2 border border-border-light dark:border-border rounded bg-white dark:bg-background-gray text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                >
                                  <option value="">Select company…</option>
                                  {allCompanies.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                  ))}
                                </select>
                                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.permissions.insightsAccess || false}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      permissions: { ...formData.permissions, insightsAccess: e.target.checked },
                                    })}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-text-primary-light dark:text-text-primary">Allow Insights access</span>
                                </label>
                              </div>
                            )}
                          </div>

                          {!formData.permissions.clientMode && (
                            <div>
                              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
                                Company Access
                                {formData.permissions.dev && (
                                  <span className="ml-2 text-xs text-success">(Dev access grants all companies)</span>
                                )}
                              </label>
                              {!formData.permissions.dev && (
                                <>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-text-muted-light dark:text-text-muted">
                                      {formData.permissions.companies?.length || 0} of {allCompanies.length} selected
                                    </span>
                                    <button
                                      type="button"
                                      onClick={toggleAllCompanies}
                                      className="text-xs text-brand-primary hover:opacity-70 transition"
                                    >
                                      {formData.permissions.companies?.length === allCompanies.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                  </div>
                                  <div className="max-h-48 overflow-y-auto border border-border-light dark:border-border rounded-lg p-2 space-y-1">
                                    {allCompanies.length > 0 ? (
                                      allCompanies.map(company => (
                                        <label key={company.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:opacity-70 transition">
                                          <input
                                            type="checkbox"
                                            checked={formData.permissions.companies?.includes(company.id)}
                                            onChange={() => toggleCompany(company.id)}
                                            className="w-4 h-4"
                                          />
                                          <span className="text-sm text-text-primary-light dark:text-text-primary">{company.name}</span>
                                          <span className="text-xs text-text-muted-light dark:text-text-muted ml-auto">{company.industry}</span>
                                        </label>
                                      ))
                                    ) : (
                                      <div className="text-sm text-text-muted-light dark:text-text-muted text-center py-2">No companies available</div>
                                    )}
                                  </div>
                                  <div className="text-xs text-text-muted-light dark:text-text-muted mt-1">Empty = all companies allowed</div>
                                </>
                              )}
                              {formData.permissions.dev && (
                                <div className="text-sm text-text-muted-light dark:text-text-muted p-3 bg-success/10 border border-success/20 rounded-lg">
                                  Dev access grants access to all companies automatically
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button variant="primary" onClick={handleSaveGroup}>Save</Button>
                        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                      </div>
                    </div>
                  ) : selectedGroup ? (
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Name:</span>
                        <p className="text-lg text-text-primary-light dark:text-text-primary">{selectedGroup.name}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Domain:</span>
                        <p className="text-lg text-text-primary-light dark:text-text-primary">{selectedGroup.domain}</p>
                      </div>
                      <div className="border-t border-border-light dark:border-border pt-4 mt-4">
                        <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Permissions:</span>
                        <div className="text-sm text-text-muted-light dark:text-text-muted mt-2 space-y-1">
                          {selectedGroup.permissions?.clientMode ? (
                            <div>Client Mode: {selectedGroup.permissions.clientCompanyName || selectedGroup.permissions.clientCompanyId || 'assigned'}</div>
                          ) : (
                            <>
                              <div>Routes: {selectedGroup.permissions?.routes?.length || 'All'}</div>
                              <div>Companies: {selectedGroup.permissions?.companies?.length || 'All'}</div>
                              <div>Dev: {selectedGroup.permissions?.dev ? 'Yes' : 'No'}</div>
                            </>
                          )}
                        </div>
                      </div>
                      {(selectedGroup.createdBy || selectedGroup.source) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {selectedGroup.source === 'booking' && (
                            <span className="text-[11px] px-1.5 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Booking</span>
                          )}
                          {selectedGroup.createdBy && (
                            <span className="text-xs text-text-muted-light dark:text-text-muted">Created by {selectedGroup.createdBy}</span>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <IconButton
                          icon={PencilIcon}
                          label="Edit Group"
                          variant="primary"
                          size="md"
                          display="icon"
                          onClick={handleEditGroup}
                        />
                        {selectedGroup.name !== 'Seytravel' && (
                          <IconButton
                            icon={TrashIcon}
                            label="Delete Group"
                            variant="danger"
                            size="md"
                            display="icon"
                            onClick={handleDeleteGroup}
                          />
                        )}
                      </div>
                    </div>
                  ) : null}
                </Card>

                {/* Right: Users List or User Details */}
                {selectedGroup && (
                  <Card>
                    {!selectedUser && editMode !== 'new-user' && editMode !== 'user' ? (
                      /* Users List */
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary">
                            Users in {selectedGroup.name}
                          </h2>
                          <IconButton
                            icon={PlusIcon}
                            label="New User"
                            variant="primary"
                            size="md"
                            display="icon"
                            onClick={handleNewUser}
                          />
                        </div>
                        <FilterableTable
                          data={usersInSelectedGroup.map(user => ({
                            ...user,
                            onClick: () => handleSelectUser(user)
                          }))}
                          columns={[
                            {
                              key: 'name',
                              header: 'Name',
                              align: 'left',
                              className: 'font-semibold text-text-primary-light dark:text-text-primary cursor-pointer hover:opacity-70 transition',
                              render: (user) => (
                                <span className="flex items-center gap-2">
                                  {user.alias || user.email}
                                  {user.source === 'booking' && (
                                    <span className="text-[11px] px-1.5 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                      Booking
                                    </span>
                                  )}
                                </span>
                              )
                            },
                            {
                              key: 'email',
                              header: 'Email',
                              align: 'left',
                              className: 'text-text-secondary-light dark:text-text-secondary',
                              render: (user) => user.email
                            }
                          ]}
                          searchFields={['alias', 'email']}
                          searchPlaceholder="Search users..."
                          defaultItemsPerPage={10}
                          cellPadding="compact"
                          showPagination={usersInSelectedGroup.length > 10}
                          loading={loading}
                        />
                      </>
                    ) : (
                      /* User Details */
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <IconButton
                            icon={ArrowLeftIcon}
                            label="Back to Users"
                            variant="secondary"
                            size="md"
                            display="icon"
                            onClick={() => {
                              setSelectedUser(null);
                              setEditMode(null);
                            }}
                          />
                          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary">
                            {editMode === 'new-user' ? 'New User' : editMode === 'user' ? 'Edit User' : 'User Details'}
                          </h2>
                        </div>

                        {(editMode === 'user' || editMode === 'new-user') ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Email</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="w-full px-3 py-2 border border-border-light dark:border-border rounded bg-white dark:bg-background-gray text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent"
                            disabled={editMode === 'user'}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Alias (Optional)</label>
                          <input
                            type="text"
                            value={formData.alias}
                            onChange={(e) => setFormData({...formData, alias: e.target.value})}
                            className="w-full px-3 py-2 border border-border-light dark:border-border rounded bg-white dark:bg-background-gray text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-transparent"
                          />
                        </div>

                        {/* User Permissions Section */}
                        <div className="border-t border-border-light dark:border-border pt-4 mt-4">
                          <h3 className="text-sm font-semibold text-text-primary-light dark:text-text-primary mb-3">User Permissions</h3>

                          <div className="space-y-4">
                            {!formData.permissions.clientMode && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-2">Routes</label>
                                  <div className="space-y-2">
                                    {availableRoutes.map(route => (
                                      <label key={route} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={formData.permissions.routes?.includes(route)}
                                          onChange={() => toggleRoute(route)}
                                          className="w-4 h-4"
                                        />
                                        <span className="text-sm text-text-primary-light dark:text-text-primary">{route}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>

                                <div>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions.dev}
                                      onChange={(e) => setFormData({
                                        ...formData,
                                        permissions: { ...formData.permissions, dev: e.target.checked }
                                      })}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-sm text-text-primary-light dark:text-text-primary">Dev Access</span>
                                  </label>
                                </div>
                              </>
                            )}

                            <div>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.clientMode || false}
                                  onChange={(e) => {
                                    const enabled = e.target.checked;
                                    const existingId = formData.permissions.clientCompanyId;
                                    setFormData({
                                      ...formData,
                                      permissions: {
                                        ...formData.permissions,
                                        clientMode: enabled,
                                        clientCompanyId: enabled ? existingId : '',
                                        clientCompanyName: enabled ? formData.permissions.clientCompanyName : '',
                                        companies: enabled && existingId ? [existingId] : formData.permissions.companies,
                                      }
                                    });
                                  }}
                                  className="w-4 h-4"
                                />
                                <span className="text-sm font-medium text-text-primary-light dark:text-text-primary">Client Mode</span>
                                <span className="text-xs text-text-muted-light dark:text-text-muted">(portal-only access)</span>
                              </label>
                              {formData.permissions.clientMode && (
                                <div className="mt-2 ml-6">
                                  <label className="block text-xs text-text-muted-light dark:text-text-muted mb-1">Assigned Company</label>
                                  <select
                                    value={formData.permissions.clientCompanyId || ''}
                                    onChange={(e) => {
                                      const company = allCompanies.find(c => c.id === e.target.value);
                                      setFormData({
                                        ...formData,
                                        permissions: {
                                          ...formData.permissions,
                                          clientCompanyId: e.target.value,
                                          clientCompanyName: company?.name || '',
                                          companies: e.target.value ? [e.target.value] : [],
                                        }
                                      });
                                    }}
                                    className="w-full text-sm px-3 py-2 border border-border-light dark:border-border rounded bg-white dark:bg-background-gray text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                  >
                                    <option value="">Select company…</option>
                                    {allCompanies.map(c => (
                                      <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                  </select>
                                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions.insightsAccess || false}
                                      onChange={(e) => setFormData({
                                        ...formData,
                                        permissions: { ...formData.permissions, insightsAccess: e.target.checked },
                                      })}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-sm text-text-primary-light dark:text-text-primary">Allow Insights access</span>
                                  </label>
                                </div>
                              )}
                            </div>

                            {!formData.permissions.clientMode && (
                              <div>
                                <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-2">
                                  Company Access
                                  {formData.permissions.dev && (
                                    <span className="ml-2 text-xs text-success">(Dev grants all)</span>
                                  )}
                                </label>
                                {!formData.permissions.dev && (
                                  <>
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs text-text-muted-light dark:text-text-muted">
                                        {formData.permissions.companies?.length || 0} of {allCompanies.length}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={toggleAllCompanies}
                                        className="text-xs text-brand-primary hover:opacity-70 transition"
                                      >
                                        {formData.permissions.companies?.length === allCompanies.length ? 'Deselect All' : 'Select All'}
                                      </button>
                                    </div>
                                    <div className="max-h-32 overflow-y-auto border border-border-light dark:border-border rounded p-2 space-y-1">
                                      {allCompanies.length > 0 ? (
                                        allCompanies.map(company => (
                                          <label key={company.id} className="flex items-center gap-2 cursor-pointer text-xs">
                                            <input
                                              type="checkbox"
                                              checked={formData.permissions.companies?.includes(company.id)}
                                              onChange={() => toggleCompany(company.id)}
                                              className="w-3 h-3"
                                            />
                                            <span className="text-text-primary-light dark:text-text-primary">{company.name}</span>
                                          </label>
                                        ))
                                      ) : (
                                        <div className="text-xs text-text-muted-light dark:text-text-muted text-center py-2">No companies available</div>
                                      )}
                                    </div>
                                    <div className="text-xs text-text-muted-light dark:text-text-muted mt-1">Empty = all allowed</div>
                                  </>
                                )}
                                {formData.permissions.dev && (
                                  <div className="text-xs text-text-muted-light dark:text-text-muted p-2 bg-success/10 border border-success/20 rounded">
                                    Dev access grants all companies
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Button variant="primary" onClick={handleSaveUser} className="w-full">Save</Button>
                          <Button variant="secondary" onClick={handleCancel} className="w-full">Cancel</Button>
                        </div>
                      </div>
                    ) : selectedUser ? (
                      <div className="space-y-4">
                        <div>
                          <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Email:</span>
                          <p className="text-text-primary-light dark:text-text-primary">{selectedUser.email}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Alias:</span>
                          <p className="text-text-primary-light dark:text-text-primary">{selectedUser.alias || 'None'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Group:</span>
                          <p className="text-text-primary-light dark:text-text-primary">{selectedGroup?.name}</p>
                        </div>
                        <div className="border-t border-border-light dark:border-border pt-4 mt-4">
                          <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">Permissions:</span>
                          <div className="text-sm text-text-muted-light dark:text-text-muted mt-2 space-y-1">
                            {selectedUser.permissions?.clientMode ? (
                              <div>Client Mode: {selectedUser.permissions.clientCompanyName || selectedUser.permissions.clientCompanyId || 'assigned'}</div>
                            ) : (
                              <>
                                <div>Routes: {selectedUser.permissions?.routes?.length || 'All'}</div>
                                <div>Companies: {selectedUser.permissions?.companies?.length || 'All'}</div>
                                <div>Dev: {selectedUser.permissions?.dev ? 'Yes' : 'No'}</div>
                              </>
                            )}
                          </div>
                        </div>
                        {(selectedUser.createdBy || selectedUser.source) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {selectedUser.source === 'booking' && (
                              <span className="text-[11px] px-1.5 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Booking</span>
                            )}
                            {selectedUser.createdBy && (
                              <span className="text-xs text-text-muted-light dark:text-text-muted">Created by {selectedUser.createdBy}</span>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <IconButton
                            icon={PencilIcon}
                            label="Edit User"
                            variant="primary"
                            size="md"
                            display="icon"
                            onClick={handleEditUser}
                          />
                          <IconButton
                            icon={TrashIcon}
                            label="Delete User"
                            variant="danger"
                            size="md"
                            display="icon"
                            onClick={handleDeleteUser}
                          />
                        </div>
                      </div>
                    ) : null}
                      </>
                    )}
                  </Card>
                )}
              </div>
            </>
          )}
      </div>

      {/* Backups Tab */}
      {(permissions?.BACKUPS_manage || permissions?.dev) && (
        <div className={activeTab !== 'backups' ? 'hidden' : ''}>
          <BackupsManager />
        </div>
      )}
    </div>
  );
}
